import {ExecutionIterationStatusType} from '../../enum/execution-iteration-status.enum';
import {LotteryStatusTranslationService} from '../../../shared/translations/lottery-status-translations.service';
import {DateObject} from '../reusables/date-object';
import {Status} from '../reusables/status';
import {PriceCategory} from './price-category';

/**
 * Execution iteration class that is part of the lottery
 */
export class ExecutionIteration {
  public '@id': string;
  public id: number;
  public priceCategory: PriceCategory;
  public capacity: number;
  public status: Status;
  public ticketRequestType: string;
  public createdAt: DateObject;
  public iterationDrawSummary: IterationDrawSummary;
  public reservationSummary: ReservationSummary;

  public statuses: Array<any> = [
    { value: ExecutionIterationStatusType.COMPLETED, status: this.t.i18nCompleted, class: 'esf-tag--success' },
    { value: ExecutionIterationStatusType.EXECUTION, status: this.t.i18nInProgress, class: 'esf-tag--warning' },
    { value: ExecutionIterationStatusType.CREATED, status: this.t.i18nCreated, class: 'esf-tag--info' },
  ];

  public types: Array<any> = [
    { value: 'primary', text: this.t.i18nPrimary },
    { value: 'secondary', text: this.t.i18nSecondary },
  ];

  constructor(executionIteration: any, private t: LotteryStatusTranslationService) {
    this['@id'] = executionIteration['@id'];
    this.id = executionIteration.id;
    this.priceCategory = executionIteration.priceCategory;
    this.capacity = executionIteration.capacity ?? 0;
    this.ticketRequestType = executionIteration.ticketRequestType;
    this.createdAt = new DateObject(executionIteration.createdAt, 'dd.MM.yyyy HH:mm');
    this.iterationDrawSummary = executionIteration.iterationDrawSummary ? new IterationDrawSummary(executionIteration.iterationDrawSummary) : null;
    this.reservationSummary = executionIteration.reservationSummary ? new ReservationSummary(executionIteration.reservationSummary, this.t) : null;
    this.status = this.getFullStatus(executionIteration.status);
  }

  public getFullStatus(status: string): any {
    const fullStatus = this.statuses.find(el => status === el.value);
    return fullStatus ? new Status(fullStatus) : status;
  }

  get translatedType(): any {
    const translatedType = this.types.find(el => this.ticketRequestType === el.value);
    return translatedType ? translatedType.text : this.ticketRequestType;
  }

  get displayStatus(): Status {
    if (this.status.value === ExecutionIterationStatusType.COMPLETED) {
      return this.reservationSummary.status;
    } else {
      return this.status;
    }
  }
}
/**
 * Iteration Draw Summary class that is part of the lottery
 */
export class IterationDrawSummary {
  public '@id': string;
  public prioritizedWinnersCount: number;
  public regularWinnersCount: number;
  public drawnTicketsCount: number;

  constructor(executionDrawSummary: any) {
    this['@id'] = executionDrawSummary['@id'];
    this.prioritizedWinnersCount = executionDrawSummary?.prioritizedWinnersCount ?? 0;
    this.regularWinnersCount = executionDrawSummary?.regularWinnersCount ?? 0;
    this.drawnTicketsCount = executionDrawSummary?.drawnTicketsCount ?? 0;
  }

  get numOfRequests(): number {
    return this.prioritizedWinnersCount + this.regularWinnersCount;
  }
  get fullRequests(): string {
    return `${this.numOfRequests} (${this.drawnTicketsCount})`;
  }
}
/**
 * Reservation Process class that is part of the lottery
 */
export class ReservationSummary {
  public '@id': string;
  public startedAt: DateObject;
  public completedAt: DateObject;
  public numberOfSuccessfulReservations: number;
  public numberOfUnsuccessfulReservations: number;
  public numberOfSuccessfullySeatedTickets: number;
  public numberOfUnsuccessfullySeatedTickets: number;
  public numberOfTotalReservations: number;
  public status: Status;

  public summaryStatuses: Array<any> = [
    { value: ExecutionIterationStatusType.COMPLETELY_SEATED, status: this.t.i18nCompletelySeated, class: 'esf-tag--success' },
    { value: ExecutionIterationStatusType.PARTIALLY_SEATED, status: this.t.i18nPartlySeated, class: 'esf-tag--warning' },
    { value: ExecutionIterationStatusType.NOTHING_SEATED, status: this.t.i18nNothingSeated, class: 'esf-tag--warning' },
  ];

  constructor(reservationSummary: any, private t: LotteryStatusTranslationService) {
    this['@id'] = reservationSummary['@id'];
    this.startedAt = new DateObject(reservationSummary.startedAt, 'dd.MM.yyyy HH:mm');
    this.completedAt = new DateObject(reservationSummary.completedAt, 'dd.MM.yyyy HH:mm');
    this.numberOfSuccessfulReservations = reservationSummary?.numberOfSuccessfulReservations ?? 0;
    this.numberOfUnsuccessfulReservations = reservationSummary?.numberOfUnsuccessfulReservations ?? 0;
    this.numberOfSuccessfullySeatedTickets = reservationSummary?.numberOfSuccessfullySeatedTickets ?? 0;
    this.numberOfUnsuccessfullySeatedTickets = reservationSummary?.numberOfUnsuccessfullySeatedTickets ?? 0;
    this.numberOfTotalReservations = reservationSummary?.numberOfTotalReservations ?? 0;
    this.status = this.getFullStatus(reservationSummary?.status);
  }

  public getFullStatus(status: string): any {
    const fullStatus = this.summaryStatuses.find(el => status === el.value);
    return fullStatus ? new Status(fullStatus) : status;
  }

  get displaySuccessful() {
    return `${this.numberOfSuccessfulReservations} (${this.numberOfSuccessfullySeatedTickets})`;
  }

  get displayUnsuccessful() {
    return `${this.numberOfUnsuccessfulReservations} (${this.numberOfUnsuccessfullySeatedTickets})`;
  }
}
